import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  Inject,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {SearchState} from '../../../../../../types/SearchState';
import {ToolsService} from '../../../../../../services/tools.service';
import {WPPostCareServiceTypePage} from '../../typings/WPPostCareServiceTypePage';
import {LoaderService} from '../../../../../../services/loader.service';
import {LoaderQueueItem} from '../../../../../../types/LoaderQueueItem';
import {finalize} from 'rxjs/operators';
import {Breadcrumb} from '../../../../../../types/Breadcrumb';
import {RoutesEnums} from '../../../../../../enums/RoutesEnums';
import {MetaTypesEnum, UrlTermsSymbolsEnum} from '../../../../../../enums/SeoEnums';
import {UrlBuilderService} from '../../../../../../services/url-builder.service';
import {BookTimeNowThemeEnum} from '../../../../../../components/book-time-now/enums/book-time-now-theme.enum';
import {DLPageTypeEnum} from '../../../../../../enums/DataLayerEnums';
import {DataLayerParamsPageViewGA4} from '../../../../../../types/DataLayerParamsPageViewGA4';
import {PageViewsGa4ResolverService} from '../../../../../../services/page-views-ga4-resolver.service';
import {AdsStateService} from '../../../../../../services/ads-state.service';
import {DfpStateData} from '../../../../../../types/DfpStateData';
import {MetaTag} from '../../../../../../types/MetaTag';
import {SeoMetaService} from '../../../../../../services/seo-meta.service';
import {WpService} from '../../../../../../services/wp.service';
import {SearchResultContent} from '../../../../../../types/SearchResultContent';
import {WindowSizeService} from '../../../../../../services/window-size.service';
import {AppConfigService} from '../../../../../../services/app-config.service';
import {SearchHelperService} from '../../../../../../services/search-helper.service';
import {GtmService} from '../../../../../../services/gtm.service';
import {SearchService} from '../../../../../../services/search/search.service';
import {CpruTabsService} from '../../../../../../services/cpru-tabs.service';
import {LayoutService} from '../../../../../layout/layout.service';
import {CpruTab} from '../../../../../../types/CpruTab';
import {DOCUMENT} from '@angular/common';
import {ElementPositionEnum} from '../../../../../../enums/CssClassEnums';
import {WPPostSymptomPage} from '../../typings/WPPostSymptomPage';
import {SearchStateFieldsEnum} from '../../../../../../enums/SearchEnums';
import {CareproviderUnitType} from '../../../../../../types/CareproviderUnitType';
import {SearchContentSchemaService} from '../../services/search-content-schema.service';
import {WPContentSectionWithButtonAndSummary} from '../../typings/WPContentSectionWithButtonAndSummary';
import {ContentLabelsEnum} from '../../enums/content-labels.enum';
import {StaticSearchContentDataV2Service} from '../../services/static-search-content-data-v2.service';

@Component({
  selector: 'app-search-content-main',
  templateUrl: './search-content-main.component.html',
  styleUrls: ['./search-content-main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchContentMainComponent implements OnInit, OnDestroy {
  public cpruTabsService = inject(CpruTabsService);
  public tabs = this.cpruTabsService.tabs;
  public highlightedSection = this.cpruTabsService.highlightedSection;

  public state = input<SearchState>();
  public onEmptyContentPage = output<boolean>();
  public pageData = signal<WPPostCareServiceTypePage | WPPostSymptomPage>(null);
  public breadcrumbs = signal<Breadcrumb[]>([]);
  public BookTimeNowThemeEnum = BookTimeNowThemeEnum;
  public searchForLabel = computed<string>(() =>
    this.pageData() ? this.pageData()?.title.rendered.toLowerCase() : '',
  );
  public filteredContentSections = computed<Array<WPContentSectionWithButtonAndSummary>>(() => {
    if (this.pageData()) {
      return this.pageData().acf.contentSections.map((contentSection) => {
        let tmpItem: WPContentSectionWithButtonAndSummary = this.tools.clone(contentSection);
        tmpItem.button.label =
          tmpItem.button.value === ContentLabelsEnum.CUSTOM ? tmpItem.customLabel : tmpItem.button.label;

        if (tmpItem.button.value === ContentLabelsEnum.OM) {
          tmpItem.button.label = 'Om ' + this.pageData().title.rendered;
        }

        tmpItem.id = this.prepareContentSectionId(tmpItem.button.label);
        return tmpItem;
      });
    } else {
      return null;
    }
  });
  protected topSectionCssClass = signal<string>(ElementPositionEnum.NORMAL);
  protected placeholderStyle = signal<{[klass: string]: any} | null | undefined>({display: 'none'});
  protected pageType = signal<DLPageTypeEnum>(null);
  protected relatedItems = signal<CareproviderUnitType[]>(null);
  protected relatedField = signal<SearchStateFieldsEnum>(null);
  protected readonly SearchStateFieldsEnum = SearchStateFieldsEnum;
  protected hideBlog = computed<boolean>(() => this.pageType() === DLPageTypeEnum.SYMPTOM_PAGE);
  private searchService = inject(SearchService);
  protected adCampaignOwner = this.searchService.adCampaignOwner;
  private metaTags = this.searchService.metaTags;
  private locationState = this.searchService.userLocationState;
  public lat = computed<number>(() => (this.locationState()?.locationSearch ? this.locationState()?.lat : null));
  public lon = computed<number>(() => (this.locationState()?.locationSearch ? this.locationState()?.lon : null));
  private searchResults = this.searchService.searchResults;
  protected searchResultContent = computed<SearchResultContent>(() => this.searchResults()?.searchResultContent);
  public customGa4Params = computed<DataLayerParamsPageViewGA4>(() => {
    if (this.pageData() && this.searchResultContent() && this.metaTags() && this.pageType()) {
      const titleTag: MetaTag = this.metaTags().find((item) => item.type === MetaTypesEnum.TITLE);

      const customGa4Params: DataLayerParamsPageViewGA4 = this.wpService.createDataLayerGa4CustomParams(
        this.pageData(),
        this.pageType(),
        titleTag ? titleTag.value : this.pageData().title.rendered,
      );

      if (this.searchResultContent().careServiceType) {
        customGa4Params.careServiceTypeId = this.searchResultContent().careServiceType.id;
      }
      if (this.searchResultContent().symptom) {
        customGa4Params.symptomId = this.searchResultContent()?.symptom.id;
      }
      customGa4Params.adCampaignOwner = this.adCampaignOwner();

      return customGa4Params;
    }
    return null;
  });
  private loadedSlug = signal<string>(null);
  private isPageViewRegisterTriggered = signal<boolean>(false);
  private windowSize = inject(WindowSizeService);
  private size = this.windowSize.windowSize;
  private readonly mobileTabsThreshold = 992;
  protected isMobile = computed<boolean>(() => this.size()[0] < this.mobileTabsThreshold);
  private readonly greyBgThreshold = 768;

  constructor(
    private pageDataService: StaticSearchContentDataV2Service,
    private tools: ToolsService,
    private loaderService: LoaderService,
    private urlBuilder: UrlBuilderService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private adsStateService: AdsStateService,
    private metaService: SeoMetaService,
    private wpService: WpService,
    private appConfigService: AppConfigService,
    private searchHelper: SearchHelperService,
    private gtmService: GtmService,
    private layoutService: LayoutService,
    private schemaService: SearchContentSchemaService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    effect(
      () => {
        if (this.searchResultContent()) {
          this.updateAdsState();
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (this.searchResultContent() && this.pageType()) {
          if (this.pageType() === DLPageTypeEnum.CARE_SERVICE_TYPE_PAGE) {
            this.relatedItems.set(this.searchResultContent()?.careServiceType?.symptoms);
          } else {
            this.relatedItems.set(this.searchResultContent()?.symptom?.careServiceTypes);
          }
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (
          this.state() &&
          this.searchHelper.shouldDisplayServiceTypePage(this.state()) === true &&
          (this.tools.isNil(this.loadedSlug()) || this.loadedSlug() !== this.state().careServiceTypeUrlPath)
        ) {
          this.pageType.set(DLPageTypeEnum.CARE_SERVICE_TYPE_PAGE);
          this.relatedField.set(SearchStateFieldsEnum.SYMPTOM);
          this.reloadData();
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (
          this.state() &&
          this.searchHelper.shouldDisplaySymptomPage(this.state()) === true &&
          (this.tools.isNil(this.loadedSlug()) || this.loadedSlug() !== this.state().symptomUrlPath)
        ) {
          this.pageType.set(DLPageTypeEnum.SYMPTOM_PAGE);
          this.relatedField.set(SearchStateFieldsEnum.CARE_SERVICE_TYPE);
          this.reloadData();
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (
          !this.isPageViewRegisterTriggered() &&
          this.pageData() &&
          this.searchResultContent() &&
          this.metaTags() &&
          this.customGa4Params()
        ) {
          this.pageViewGa4Resolver.trackPageView(this.customGa4Params());
          this.isPageViewRegisterTriggered.set(true);
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (this.pageData() && this.pageData() !== null) {
          this.schemaService.removeSchema();
          this.schemaService.addSchema(this.pageData());
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (this.filteredContentSections()) {
          this.updateTabs();
        }
      },
      {allowSignalWrites: true},
    );

    effect(() => {
      if (this.metaTags()) {
        this.metaService.updateMetaTags(this.metaTags());
      }
    });

    effect(
      () => {
        this.layoutService.setShouldUseGreyBackground(this.size()[0] >= this.greyBgThreshold);
      },
      {allowSignalWrites: true},
    );
  }

  @HostListener('window:scroll', ['$event'])
  public onScroll() {
    if (this.tabs() && this.tabs().length > 0) {
      const verticalOffset =
        window.pageYOffset || this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;

      const coerce = 200;
      let currentSection: CpruTab = null;

      for (let i = this.tabs().length - 1; i >= 0; i--) {
        const elem = this.document.getElementById(`section-${this.tabs()[i].name}`);
        if (elem && verticalOffset + coerce > elem.offsetTop) {
          currentSection = this.tabs()[i];
          break;
        }
      }
      if (currentSection && currentSection.name != this.highlightedSection()) {
        this.cpruTabsService.setHighlightedSection(currentSection.name);
      }
    }
  }

  ngOnInit() {
    this.isPageViewRegisterTriggered.set(false);
  }

  ngOnDestroy() {
    this.schemaService.removeSchema();
    this.isPageViewRegisterTriggered.set(false);
    this.pageData.set(null);
    this.cpruTabsService.updateTabs([]);
  }

  public handleTabsInViewport({target, visible}: {target: Element; visible: boolean}): void {
    if (visible) {
      this.topSectionCssClass.set(ElementPositionEnum.NORMAL);
      this.placeholderStyle.set({display: 'none'});
    } else {
      const componentElement: HTMLElement = this.document.getElementById('top-section-container') as HTMLElement;
      const height = componentElement.offsetHeight;
      this.topSectionCssClass.set(ElementPositionEnum.FIXED_WITH_SEARCH);
      this.placeholderStyle.set({display: 'block', height: `${height}px`});
    }
  }

  private reloadData(): void {
    this.isPageViewRegisterTriggered.set(false);
    const queryId1: number = this.loaderService.generateQueueId();
    const queryItem1: LoaderQueueItem = {
      id: queryId1,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem1);
    if (this.pageType() === DLPageTypeEnum.CARE_SERVICE_TYPE_PAGE) {
      this.loadedSlug.set(this.tools.clone(this.state().careServiceTypeUrlPath));
      if (this.state().careServiceTypeUrlPath !== '') {
        this.pageDataService
          .GetCareServicePostBySlug(this.state().careServiceTypeUrlPath)
          .pipe(
            finalize(() => {
              this.loaderService.removeFromQueue(queryId1);
            }),
          )
          .subscribe(
            (postData) => this.handlePageData(postData),
            (error) => console.error(error),
          );
      }
    }
    if (this.pageType() === DLPageTypeEnum.SYMPTOM_PAGE) {
      this.loadedSlug.set(this.tools.clone(this.state().symptomUrlPath));
      if (this.state().symptomUrlPath !== '') {
        this.pageDataService
          .GetSymptomPostBySlug(this.state().symptomUrlPath)
          .pipe(
            finalize(() => {
              this.loaderService.removeFromQueue(queryId1);
            }),
          )
          .subscribe(
            (postData) => this.handlePageData(postData),
            (error) => console.error(error),
          );
      }
    }
  }

  private handlePageData(postData: WPPostCareServiceTypePage | WPPostSymptomPage): void {
    if (!postData) {
      this.redirectToSearchResults();
    } else {
      this.pageData.set(postData);
      this.gtmService.pushTag({ecommerce: null}, true);
      this.updateMetaTags();
      this.buildBreadcrumbs();
      // this.searchWidgetManager.getUserLocation(null, false, false, false);
    }
  }

  private buildBreadcrumbs() {
    this.breadcrumbs.set([]);
    const paramsPath: string[] = ['/', RoutesEnums.SEARCH];

    if (this.state().careServiceTypeUrlPath !== '') {
      paramsPath.push(`${this.state().careServiceTypeUrlPath}-${UrlTermsSymbolsEnum.SERVICE_TYPE}`);

      this.breadcrumbs.update((prev) => [
        ...prev,
        {
          routeParams: paramsPath,
          routeQueryParams: null,
          anchor: this.pageData()?.title?.rendered,
        },
      ]);
    }

    if (this.state().symptomUrlPath !== '') {
      paramsPath.push(`${this.state().symptomUrlPath}-${UrlTermsSymbolsEnum.SYMPTOM}`);
      this.breadcrumbs.update((prev) => [
        ...prev,
        {
          routeParams: paramsPath,
          routeQueryParams: null,
          anchor: this.pageData()?.title?.rendered,
        },
      ]);
    }
  }

  private redirectToSearchResults(): void {
    this.onEmptyContentPage.emit(true);
  }

  private updateAdsState() {
    const dfpState: DfpStateData = this.adsStateService.getDefaultDfpState();
    if (!this.tools.isNil(this.searchResultContent().caregiverUnitType)) {
      dfpState.careproviderUnitTypeId = this.searchResultContent().caregiverUnitType.id;
    }
    if (!this.tools.isNil(this.searchResultContent().careServiceType)) {
      dfpState.careServiceTypeId = this.searchResultContent().careServiceType.id;
    }
    if (!this.tools.isNil(this.searchResultContent().symptom)) {
      dfpState.symptomId = this.searchResultContent().symptom.id;
    }
    if (!this.tools.isNil(this.searchResultContent().place)) {
      dfpState.placeId = this.searchResultContent().place.id;
    }
    dfpState.pageModule = RoutesEnums.SEARCH;
    this.adsStateService.updateAdsState(dfpState);
  }

  private updateMetaTags(): void {
    const metaTags: MetaTag[] = [];
    const url = this.urlBuilder.CreateStaticLink(
      this.urlBuilder.CreateSearchResultsRouterLink(this.state(), ['/', RoutesEnums.SEARCH]),
    );

    metaTags.push({
      value: 'website',
      type: 'property',
      field: 'og:type',
    });

    metaTags.push({
      value: url,
      type: 'property',
      field: 'og:url',
    });

    metaTags.push({
      value: this.appConfigService.appConfig.siteName,
      type: 'property',
      field: 'og:site_name',
    });

    if (
      !this.tools.isNil(this.pageData().acf.backgroundImageMobile) &&
      this.pageData().acf.backgroundImageMobile.id > 0
    ) {
      metaTags.push({
        value: this.wpService.getImageFlatUrl(this.pageData().acf.backgroundImageMobile),
        type: 'property',
        field: 'og:image',
      });

      metaTags.push({
        value: this.wpService.getImageFlatUrl(this.pageData().acf.backgroundImageMobile),
        type: 'name',
        field: 'twitter:image',
      });

      metaTags.push({
        value: 'summary_large_image',
        type: 'name',
        field: 'twitter:card',
      });
    }

    this.metaService.updateMetaTags(metaTags);
  }

  private updateTabs(): void {
    const tabs: CpruTab[] = [];

    this.filteredContentSections().forEach((section) => {
      tabs.push({
        name: section.button.value,
        label: section.button.label,
        mobileOnly: false,
        desktopOnly: false,
      });
    });

    this.cpruTabsService.updateTabs(tabs);
  }

  private prepareContentSectionId(label: string): string {
    return this.urlBuilder.NormalizeText(label, true);
  }
}
